export default class SecureAuthenticator {
  constructor(authenticationData, masterKey) {
    this.secureData = authenticationData;
    this.masterKey = masterKey;

    this.#initializeSecurity();
  }

  async #hashInput(value, salt) {
    const encoder = new TextEncoder();
    const saltArray = this.#hexToArray(salt);
    const data = encoder.encode(value);
    const saltedData = new Uint8Array([...data, ...saltArray]);

    let hashedData = saltedData;
    for (let index = 0; index < 1000; index++) {
      hashedData = new Uint8Array(await crypto.subtle.digest('SHA-256', hashedData));
    }

    return hashedData;
  }

  #secureCompare(a, b) {
    if (a.length !== b.length) return false;
    let result = 0;
    for (const [index, element] of a.entries()) {
      result |= element ^ b[index];
    }
    return result === 0;
  }

  #hexToArray(hex) {
    const pairs = hex.match(/[\da-f]{2}/gi) || [];
    return new Uint8Array(pairs.map((h) => Number.parseInt(h, 16)));
  }

  async #generateKey(masterKey) {
    const keyData = this.#hexToArray(masterKey);
    return await crypto.subtle.importKey('raw', keyData, { name: 'AES-GCM' }, false, ['decrypt']);
  }

  async #verifyHash(hashedUsername, hashedPassword) {
    try {
      const key = await this.#generateKey(this.masterKey);
      const iv = this.#hexToArray(this.secureData.iv);
      const encryptedData = this.#hexToArray(this.secureData.encryptedCredentials);

      const decryptedData = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        key,
        encryptedData
      );

      const combined = new Uint8Array([...hashedUsername, ...hashedPassword]);
      return this.#secureCompare(combined, new Uint8Array(decryptedData));
    } catch (error) {
      console.error('Verification error:', error);
      return false;
    }
  }

  #initializeSecurity() {
    if (!this.secureData || !this.masterKey) {
      throw new Error('Invalid security configuration');
    }
  }

  async verifyCredentials(username, password) {
    try {
      const hashedUsername = await this.#hashInput(username, this.secureData.usernameSalt);
      const hashedPassword = await this.#hashInput(password, this.secureData.passwordSalt);
      return await this.#verifyHash(hashedUsername, hashedPassword);
    } catch (error) {
      console.error('Authentication error:', error);
      throw error;
    }
  }
}
